/**
 * Linking a trouble ticket back to the repair order it was raised for.
 *
 * `cms.troubleticket` has **no `repairorderrecno` column** — its columns are
 * recno, pronumber, userid, datetime, category, issue, responsible, reason,
 * assignedto, resolve, resolvedatetime, carriercode, driverticket,
 * bookingnumber, preventable, servicefailure. The only link is the RO number
 * the auto-created ticket writes into the free-text `issue`:
 *
 *   Repair Order #84958 has been created for pro 5077709.
 *   Repair Issue: Mechanical - Other (Pictures Required)
 *   Hub Axles seal leaking Right side
 *
 * Matching on `pronumber` is WRONG and must never be used as a fallback: a PRO
 * can carry several repair orders, so a pronumber cannot say WHICH repair order
 * a ticket is about.
 *
 * Measured against production (2026 to date): 8,214 tickets with
 * category = 'REPAIR ORDER'; 8,021 (97.6%) contain `Repair Order #<digits>`,
 * and all 3,792 of those checked resolved to a real
 * `safetynew.repairorder.recno` — zero dangling. The remaining ~193
 * (15-38/month) are HAND-WRITTEN tickets ("NOT DRIVEABLE DUE TO BAD CHASSIS
 * TIRE…") with no repair order behind them, so rendering no controls for those
 * is CORRECT, not a degradation.
 */

/** The category the auto-created repair-order tickets are filed under. */
export const REPAIR_ORDER_TICKET_CATEGORY = 'REPAIR ORDER';

/**
 * `Repair Order #<digits>`, case-insensitive, tolerating extra whitespace
 * around the `#` and the number. Not anchored: the phrase sits at the start of
 * the auto-generated issue today, but the rest of the text is user-supplied.
 */
const RO_NUMBER_RE = /repair\s+order\s*#\s*(\d+)/i;

/** The repair order a trouble ticket's issue text refers to, or null. */
export const parseRepairOrderRecno = (issue: string | null | undefined): number | null => {
  if (!issue) return null;
  const match = RO_NUMBER_RE.exec(issue);
  if (!match) return null;
  const recno = Number(match[1]);
  // 0 is not a real recno, and a NaN here could only come from an overflowing
  // digit run — either way there is nothing to ping.
  if (!Number.isSafeInteger(recno) || recno <= 0) return null;
  return recno;
};

/**
 * The repair order a trouble ticket refers to, or null. Both gates matter: the
 * ticket must be a REPAIR ORDER ticket AND its issue must name an RO, so a
 * ticket of another category that happens to quote a repair-order number does
 * not grow maintenance-ping controls.
 */
export const repairOrderRecnoForTicket = (ticket: {
  category?: string | null;
  issue?: string | null;
}): number | null => {
  const category = (ticket.category || '').trim().toUpperCase();
  if (category !== REPAIR_ORDER_TICKET_CATEGORY) return null;
  return parseRepairOrderRecno(ticket.issue);
};
